import { SEARCH_TYPES } from 'app/constants';
import React, { useState } from 'react';
import styled from 'styled-components';
import { getStayOptions } from 'datamodel/SearchFilters/utils';
import Actions from 'datamodel/SearchFilters/actions';
import { useDispatch } from 'react-redux';
import SimpleSearchBar from 'app/Sections/Home/components/simple-search-bar';
import { getPublicAsset } from 'base/shared/Utils';
import { translate } from 'base/shared/Localization';
import Header from '../Header';

const SwitcherContainer = styled.div`
  display: flex;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 25px;
  padding: 0;
  gap: 0;
  width: fit-content;
  backdrop-filter: blur(10px);
`;

const SwitcherButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  background: ${(props) => {
    if (!props.$isActive) return 'transparent';
    if (props.$option === 'all-rooms') return '#06BD8C';
    return 'linear-gradient(135deg, #FFE193 0%, #FFC55E 100%)';
  }};
  color: ${(props) => {
    if (!props.$isActive) return 'rgba(255, 255, 255, 0.8)';
    if (props.$option === 'badi-plus') return 'black';
    return 'white';
  }};
  &:hover {
    background: ${(props) => {
      if (props.$isActive) {
        if (props.$option === 'all-rooms') return '#05a378';
        return 'linear-gradient(135deg, #ffd670 0%, #ffb340 100%)';
      }
      return 'rgba(255, 255, 255, 0.1)';
    }};
    color: ${(props) => {
      if (props.$isActive) {
        if (props.$option === 'badi-plus') return 'black';
        return 'white';
      }
      return 'rgba(255, 255, 255, 1)';
    }};
  }
  &:focus {
    outline: none;
    box-shadow: ${(props) => {
      if (props.$option === 'all-rooms') {
        return '0 0 0 2px rgba(6, 189, 140, 0.3)';
      }
      return '0 0 0 2px rgba(255, 193, 94, 0.3)';
    }};
  }
`;

const BackgroundImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 240px; /* Optional: for very small screens */
  max-height: 500px; /* Optional: to avoid it being too tall on huge screens */
  padding-top: 80px; /* Space for the fixed header */
  background-image: url('${(props) => props.$imageUrl}');
  background-size: cover;
  background-position: center;
  transition: background-image 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 0;
  }

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
    padding-top: 70px;
  }
  @media (max-width: 480px) {
    aspect-ratio: 16 / 12;
    padding-top: 60px;
  }
`;

const SwitcherOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const Title = styled.h1`
  color: white;
  font-size: 48px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: opacity 0.3s ease;
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 24px;
  }
  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 20px;
  }
`;

// --- Positioner for the search bar ---
const SearchBarPositioner = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 30%);
  z-index: 10;
  width: 700px;
  max-width: 90vw;
`;

export default function RoomSwitcher({
  activeOption,
  onOptionChange,
  defaultOption = 'all-rooms',
}) {
  const [searchType, setSearchType] = useState(SEARCH_TYPES.ROOM);
  const dispatch = useDispatch();
  const stayOptions = getStayOptions();
  const setParams = (searchFilters) => {
    dispatch(
      Actions.setParams({
        searchFilters: { ...searchFilters, updatedFilters: true },
      })
    );
  };

  const currentOption = activeOption ?? defaultOption;
  const backgroundImage =
    currentOption === 'badi-plus'
      ? getPublicAsset('v2/images/homepage-header-badi-plus.jpg')
      : getPublicAsset('v2/images/homepage-header-badi.jpg');
  const pageTitle =
    currentOption === 'badi-plus'
      ? translate('v2.home.switcher.badiPlus')
      : translate('v2.home.switcher.findAPlace');

  const handleOptionChange = (option) => {
    onOptionChange?.(option);
  };

  return (
    <>
      <Header />
      <BackgroundImage $imageUrl={backgroundImage}>
        <SwitcherOverlay>
          <Title>{pageTitle}</Title>
          <SwitcherContainer>
            <SwitcherButton
              $isActive={currentOption === 'all-rooms'}
              $option="all-rooms"
              onClick={() => handleOptionChange('all-rooms')}
            >
              {translate('v2.home.switcher.allRooms')}
            </SwitcherButton>
            <SwitcherButton
              $isActive={currentOption === 'badi-plus'}
              $option="badi-plus"
              onClick={() => handleOptionChange('badi-plus')}
            >
              {translate('v2.home.switcher.badiPlusShort')}
            </SwitcherButton>
          </SwitcherContainer>
        </SwitcherOverlay>
        <SearchBarPositioner>
          <SimpleSearchBar
            stayOptions={stayOptions}
            setParams={setParams}
            searchType={searchType}
            setSearchType={setSearchType}
            title={translate('v2.home.switcher.findPerfectHome')}
            isBadiPlus={currentOption === 'badi-plus'}
          />
        </SearchBarPositioner>
      </BackgroundImage>
    </>
  );
}
